/**
 * Canonical date parsing used across the app. Accepts the formats a user may type by hand
 * (day-first `dd.MM.yyyy` / `dd/MM/yyyy`, or ISO-ish `yyyy-MM-dd`) as well as the output of
 * `formatDate`, and falls back to the native `Date` parser for anything else (e.g. full ISO
 * strings). Returns `null` when the input cannot be turned into a valid date.
 */
export function parseDate(value: string | Date | null | undefined): Date | null {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (!value) return null;

  const input = value.toString().trim();
  if (!input) return null;

  const patterns: { readonly re: RegExp; readonly order: 'dmy' | 'ymd' }[] = [
    { re: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, order: 'dmy' },
    { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: 'dmy' },
    { re: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: 'ymd' },
  ];

  for (const { re, order } of patterns) {
    const match = input.match(re);
    if (!match) continue;

    const [, a, b, c] = match;
    const [year, month, day] = order === 'ymd' ? [+a, +b, +c] : [+c, +b, +a];
    const date = new Date(year, month - 1, day);
    const isExact = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

    return isExact ? date : null;
  }

  const fallback = new Date(input);

  return isNaN(fallback.getTime()) ? null : fallback;
}
